import { Link } from "react-router-dom";
import { Hexagon, DoorOpen, Users, SlidersHorizontal, Rocket, ArrowRight } from "lucide-react";

const benefits = [
  { icon: DoorOpen, title: "Lower Entry Barrier", desc: "Start small and participate in funding rounds without large upfront capital." },
  { icon: Users, title: "Community-Powered Funding", desc: "Raise capital through a network of members backing ideas together." },
  { icon: SlidersHorizontal, title: "Flexible Contribution Amounts", desc: "Contribute at your own pace with amounts that fit your budget." },
  { icon: Rocket, title: "Early Access to Innovation", desc: "Get in early on emerging projects before they reach the wider market." },
];

export default function Landing() {
  return (
    <div className="app-bg min-h-screen text-white">
      <header className="flex items-center justify-between px-6 sm:px-10 h-20 max-w-7xl mx-auto">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500">
            <Hexagon className="h-5 w-5 text-slate-900" />
          </span>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-lg">Rightwayp2p</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-slate-300 hover:text-white px-4 py-2">Login</Link>
          <Link to="/register" data-testid="nav-register" className="text-sm font-semibold bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-5 py-2.5 rounded-xl transition-colors">Get Started</Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <h1 data-testid="hero-heading" className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              Empowering Innovation<br />
              Through <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Smart Crowdfunding.</span>
            </h1>
            <p className="text-slate-400 mt-6 text-base sm:text-lg max-w-lg leading-relaxed">
              Crowdfunding enables startups and innovators to raise capital efficiently while offering
              investors transparent, flexible, and community-driven opportunities.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/register" data-testid="hero-get-started" className="inline-flex items-center gap-2 text-sm font-semibold bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-6 py-3.5 rounded-xl transition-colors">
                Create Account <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/login" data-testid="hero-login" className="inline-flex items-center gap-2 text-sm font-semibold border border-white/15 hover:border-emerald-500/40 text-white px-6 py-3.5 rounded-xl transition-colors">
                Member Login
              </Link>
            </div>
          </div>
          <div className="relative fade-up">
            <div className="glass rounded-3xl p-6 border border-white/10">
              <video
                src="/aquarium.mp4"
                poster="https://images.unsplash.com/photo-1706114350113-3063f27619c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                autoPlay
                muted
                loop
                playsInline
                data-testid="aquarium-video"
                className="rounded-2xl w-full h-96 object-cover opacity-95"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-28">
        <h2 className="font-display text-2xl sm:text-3xl font-bold mb-3 text-center">
          Benefits of <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Crowdfunding</span>
        </h2>
        <p className="text-slate-400 mb-10 max-w-2xl mx-auto text-center">
          Crowdfunding enables startups and innovators to raise capital efficiently while offering
          investors flexible and community-driven opportunities.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {benefits.map((f) => (
            <div key={f.title} data-testid={`benefit-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`} className="glass rounded-2xl p-6 card-hover">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/10 text-emerald-400 border border-emerald-500/30 mb-4">
                <f.icon className="h-5 w-5" />
              </span>
              <h3 className="font-semibold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-sm text-slate-500">
        © {new Date().getFullYear()} Rightwayp2p
      </footer>
    </div>
  );
}
